/**
 * Stock Availability (API-driven)
 *
 * Fetches per-location stock from the dataconnect-backed endpoint
 *   /products/api/stock-availability/?variant_id=<id>
 * and renders a preview + drawer that separates:
 *   - shippable (eCommerce fulfillment) → "In stock, ships free"
 *   - in-store only (retail stores)      → "Out of stock online — available at <store>"
 *
 * Distance is computed client-side with the Haversine formula (no Google Maps
 * dependency). Coordinates/address/hours come entirely from the API, so the old
 * store-addresses-config.js and Shopify store_availabilities are no longer used.
 */
(function () {
  const API_BASE = 'https://shopify-manager-grc7a7fpbee0eqdx.canadaeast-01.azurewebsites.net';

  if (customElements.get('stock-availability')) return;

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));

  function haversine(lat1, lng1, lat2, lng2) {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function formatDistance(m) {
    if (m == null) return '';
    return m < 1000 ? `${Math.round(m)} m away` : `${(m / 1000).toFixed(1)} km away`;
  }

  // Cap the displayed on-hand quantity so we never advertise large stock levels.
  const QTY_CAP = 10;
  function qtyLabel(qty) {
    if (qty >= QTY_CAP) return `${QTY_CAP}+ available`;
    return `${qty} available`;
  }

  const TEAL = '#00aa90'; // brand teal
  const ICONS = {
    available: `<circle cx="8" cy="8" r="8" fill="${TEAL}"/><path d="M6.5 8.5L7.5 9.5L10.5 6.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
    unavailable: '<circle cx="8" cy="8" r="8" fill="#dc2626"/><path d="M6 6L10 10M10 6L6 10" stroke="white" stroke-width="1.5" stroke-linecap="round"/>'
  };

  customElements.define('stock-availability', class extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.apiBase = this.getAttribute('data-api-base') || API_BASE;
      this.userLocation = this._loadLocation();
      this.fetchAvailability(this.getAttribute('data-variant-id'));
    }

    // Called by the variant picker when the selection changes.
    update(variant) {
      if (!variant || !variant.id) return;
      this.setAttribute('data-variant-id', variant.id);
      this.fetchAvailability(variant.id);
    }

    _loadLocation() {
      try {
        const saved = localStorage.getItem('userLocation');
        if (saved) {
          const loc = JSON.parse(saved);
          // Purge the old fake "Regina default" a previous version saved on decline,
          // so affected browsers fall back to the clean opt-in flow.
          if (loc && loc.lat === 50.4452 && loc.lng === -104.6189) {
            localStorage.removeItem('userLocation');
            return null;
          }
          return loc;
        }
      } catch (e) { /* ignore */ }
      return null;
    }

    async fetchAvailability(variantId) {
      if (!variantId) return;
      try {
        const res = await fetch(`${this.apiBase}/products/api/stock-availability/?variant_id=${encodeURIComponent(variantId)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'unknown');
        this.data = data;
        this.render();
        // Distances are opt-in — we do NOT auto-prompt for geolocation on load.
        // The user enables them via the "Show distances" button in the drawer.
      } catch (e) {
        this.renderError();
      }
    }

    // Opt-in geolocation. On decline/unavailable we show NO distances (never a
    // fake fallback) and surface a hint that location is off.
    _requestLocation() {
      if (!navigator.geolocation) { this._locError = true; this.render(); this._reopenDrawer(); return; }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.userLocation = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          this._locError = false;
          try { localStorage.setItem('userLocation', JSON.stringify(this.userLocation)); } catch (e) {}
          this.render();
          this._reopenDrawer();
        },
        () => {
          this._locError = true;   // declined or unavailable — no fake distances
          this.render();
          this._reopenDrawer();
        },
        { timeout: 8000, enableHighAccuracy: false }
      );
    }

    // render() rebuilds the drawer (closed); re-open it if the user was in it.
    _reopenDrawer() {
      this.querySelector('.stock-availability-button')?.click();
    }

    _withDistance(loc) {
      if (this.userLocation && loc.lat != null && loc.lng != null) {
        loc._dist = haversine(this.userLocation.lat, this.userLocation.lng, loc.lat, loc.lng);
      } else {
        loc._dist = null;
      }
      return loc;
    }

    render() {
      const data = this.data;
      if (!data) return;

      const stores = (data.locations || [])
        .filter((l) => l.type === 'store' && l.qty > 0)
        .map((l) => this._withDistance(l))
        .sort((a, b) => (a._dist == null ? Infinity : a._dist) - (b._dist == null ? Infinity : b._dist));

      const shipLocs = (data.locations || []).filter((l) => l.shippable && l.qty > 0);
      const shippable = data.shippable;

      // ---- Preview (the inline line under the buy area) ----
      let iconKey, mainText, subText, buttonText;
      if (shippable) {
        iconKey = 'available';
        mainText = 'In stock online';
        subText = stores.length ? `Also available in store at ${stores.length} location${stores.length === 1 ? '' : 's'}` : '';
        buttonText = 'Check availability';
      } else if (stores.length) {
        // Out of stock online but in a store — lead with the POSITIVE (teal).
        iconKey = 'available';
        if (this.userLocation) {
          const nearest = stores[0];
          mainText = `Available in store at ${nearest.name}`;
          const parts = [];
          if (nearest._dist != null) parts.push(formatDistance(nearest._dist));
          if (stores.length > 1) parts.push(`+${stores.length - 1} more location${stores.length - 1 === 1 ? '' : 's'}`);
          subText = parts.join(' · ');
        } else {
          mainText = `Available in store at ${stores.length} location${stores.length === 1 ? '' : 's'}`;
          subText = '';
        }
        buttonText = `Find in ${stores.length} store${stores.length === 1 ? '' : 's'}`;
      } else {
        iconKey = 'unavailable';
        mainText = 'Out of stock';
        subText = 'Currently unavailable online and in stores';
        buttonText = null;
      }

      const drawerId = 'StockAvailabilityDrawer';
      this.innerHTML = `
        <div class="stock-availability-preview">
          <div class="stock-availability-info">
            <div class="stock-icon-container">
              <svg class="stock-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">${ICONS[iconKey]}</svg>
            </div>
            <div class="stock-text">
              <p class="stock-main-text">${esc(mainText)}</p>
              ${subText ? `<small class="stock-distance">${esc(subText)}</small>` : ''}
            </div>
          </div>
          ${buttonText ? `<button type="button" class="stock-availability-button" aria-controls="${drawerId}" aria-expanded="false"><span class="stock-button-text">${esc(buttonText)}</span></button>` : ''}
        </div>`;
      this.removeAttribute('hidden');

      // ---- Drawer (full list) ----
      this._renderDrawer(drawerId, shipLocs, stores);
    }

    _renderDrawer(drawerId, shipLocs, stores) {
      document.querySelectorAll('#' + drawerId).forEach((d) => d.remove());
      if (!shipLocs.length && !stores.length) return;

      const pinSvg = '<svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M8 1C5.243 1 3 3.243 3 6c0 3.75 4.4 8.44 4.588 8.638a.55.55 0 0 0 .824 0C8.6 14.44 13 9.75 13 6c0-2.757-2.243-5-5-5zm0 6.75A1.75 1.75 0 1 1 8 4.25a1.75 1.75 0 0 1 0 3.5z"/></svg>';

      // Opt-in distance control — only shown when there are stores and no location yet.
      let locHtml = '';
      if (stores.length && !this.userLocation) {
        locHtml = `<div style="display:flex;flex-direction:column;align-items:flex-end;margin-bottom:20px;">
          <button type="button" is="hover-button" class="js-use-location button button--primary" style="--color-button-background:0 170 144;--color-button-text:255 255 255;--color-button-border:0 170 144;">
            <span class="btn-fill" data-fill></span>
            <span class="btn-text" style="display:inline-flex;align-items:center;gap:8px;">${pinSvg} ${this._locError ? 'Try again' : 'Show distances from me'}</span>
          </button>
          ${this._locError ? `<p class="text-sm text-gray-600" style="text-align:right;margin-top:8px;white-space:nowrap;">Location access is blocked for this site.</p>` : ''}
        </div>`;
      }

      const shipHtml = shipLocs.length ? `
        <div class="stock-ship-section" style="margin-bottom: 28px;">
          <p class="text-sm text-opacity uppercase leading-none" style="margin-bottom: 10px;">Online</p>
          <div class="stock-location-item grid gap-2 p-4 border border-gray-200 rounded-lg">
            <div class="stock-location-header flex justify-between items-start">
              <h4 class="stock-location-name font-medium md:text-lg">Available online</h4>
              <div class="stock-location-status flex gap-2 items-center">
                <svg class="stock-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">${ICONS.available}</svg>
                <span class="stock-status-text text-sm">In stock</span>
              </div>
            </div>
            <p class="text-sm text-gray-600">Ready to order for delivery.</p>
          </div>
        </div>` : '';

      const storesHtml = stores.map((loc) => {
        const hours = loc.hours && loc.hours[new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()];
        return `
          <li class="stock-location-item p-4 border border-gray-200 rounded-lg">
            <div class="stock-location-header flex justify-between items-start">
              <h4 class="stock-location-name font-medium leading-tight md:text-lg">${esc(loc.name)}</h4>
              <div class="stock-location-status flex gap-2 items-center shrink-0" style="margin-left: 12px;">
                <svg class="stock-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">${ICONS.available}</svg>
                <span class="stock-status-text text-sm leading-tight">${esc(qtyLabel(loc.qty))}</span>
              </div>
            </div>
            ${loc._dist != null ? `<div class="stock-location-distance text-sm text-gray-600" style="margin-top: 4px;">${esc(formatDistance(loc._dist))}</div>` : ''}
            ${loc.address ? `<address class="stock-location-address text-sm text-gray-600 not-italic" style="margin-top: 14px; line-height: 1.5;">
              <div>${esc(loc.address)}</div>
              ${loc.phone ? `<div style="margin-top: 6px;">${esc(loc.phone)}</div>` : ''}
              ${hours ? `<div style="margin-top: 6px;">Today: ${esc(hours.opens)}–${esc(hours.closes)}</div>` : ''}
            </address>` : ''}
            ${loc.map_url ? `<a class="stock-location-directions" href="${esc(loc.map_url)}" target="_blank" rel="noopener" style="display: inline-flex; align-items: center; gap: 6px; margin-top: 14px; padding: 7px 14px; border: 1.5px solid ${TEAL}; color: ${TEAL}; background: #fff; border-radius: 999px; font-size: 13px; font-weight: 500; line-height: 1; text-decoration: none;">${pinSvg} Get directions</a>` : ''}
          </li>`;
      }).join('');

      const drawer = document.createElement('div');
      drawer.innerHTML = `
        <drawer-element id="${drawerId}" class="stock-availability-drawer drawer drawer--end z-45 fixed bottom-0 left-0 h-full w-full pointer-events-none" role="dialog" aria-modal="true" aria-label="Stock Availability" hidden>
          <overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none" aria-controls="${drawerId}" aria-expanded="false"></overlay-element>
          <div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">
            <gesture-element class="drawer__header flex justify-between opacity-0 invisible relative">
              <span class="drawer__title heading lg:text-3xl text-2xl leading-none tracking-tight">Availability</span>
              <button class="button button--secondary button--close drawer__close hidden sm:flex items-center justify-center" type="button" is="hover-button" aria-controls="${drawerId}" aria-expanded="false" aria-label="Close">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></span>
              </button>
            </gesture-element>
            <div class="drawer__content opacity-0 invisible flex flex-col h-full grow shrink">
              <div class="drawer__scrollable relative flex flex-col grow shrink">
                ${locHtml}
                ${shipHtml}
                ${stores.length ? `<p class="text-sm text-opacity uppercase leading-none" style="margin-bottom: 14px;">In store</p><ul class="stock-locations-list grid gap-4 md:gap-5">${storesHtml}</ul>` : ''}
              </div>
            </div>
          </div>
        </drawer-element>`;
      const el = drawer.firstElementChild;
      el.querySelector('.js-use-location')?.addEventListener('click', () => this._requestLocation());
      document.body.appendChild(el);
    }

    renderError() {
      // Fail quietly — don't show a broken widget on the product page.
      this.innerHTML = '';
      this.setAttribute('hidden', '');
    }
  });
})();
